/**
 * Every Firestore path the client uses, in one place.
 *
 * Tenant-owned data lives under `/users/{uid}/…`, so ownership is a property
 * of the document's path rather than of its contents. It used to be a `userID`
 * field, which meant every query had to remember a matching filter and every
 * composite index had to lead with the same column.
 *
 * Centralising the paths is what made that cutover a change to this file
 * rather than a sweep across thirty: call sites already threaded the uid
 * through, so only the bodies below moved.
 *
 * Ownership is the path and nothing else: no query filters on it, and the
 * documents no longer carry a `userID` field.
 *
 * See docs/plans/2026-08-08-users-subcollections-migration-design.md.
 */

import {
  collection,
  doc,
  CollectionReference,
  DocumentData,
  DocumentReference,
} from 'firebase/firestore';

import { db } from '../firebase';

// ============================================================
// Internals — the three places the migration actually lands
// ============================================================

const tenantCollection = (
  uid: string,
  name: string
): CollectionReference<DocumentData> => collection(db, 'users', uid, name);

const tenantDocument = (
  uid: string,
  name: string,
  id: string
): DocumentReference<DocumentData> => doc(db, 'users', uid, name, id);

// ============================================================
// Clients
// ============================================================

export const clientsCollection = (uid: string) =>
  tenantCollection(uid, 'clients');

export const clientDocument = (uid: string, clientId: string) =>
  tenantDocument(uid, 'clients', clientId);

// ============================================================
// Products
// ============================================================

export const productsCollection = (uid: string) =>
  tenantCollection(uid, 'products');

export const productDocument = (uid: string, productId: string) =>
  tenantDocument(uid, 'products', productId);

// ============================================================
// Categories
// ============================================================

export const categoriesCollection = (uid: string) =>
  tenantCollection(uid, 'categories');

export const categoryDocument = (uid: string, categoryId: string) =>
  tenantDocument(uid, 'categories', categoryId);

// ============================================================
// Receipts
// ============================================================

export const receiptsCollection = (uid: string) =>
  tenantCollection(uid, 'receipts');

export const receiptDocument = (uid: string, receiptId: string) =>
  tenantDocument(uid, 'receipts', receiptId);

// ============================================================
// Price history
// ============================================================

export const priceHistoryCollection = (uid: string) =>
  tenantCollection(uid, 'priceHistory');

// ============================================================
// Receipt number reservations
// ============================================================

// Firestore doc IDs cannot contain '/'
export const receiptNumberKey = (number: string): string =>
  number.replace(/\//g, '-');

/**
 * Reservation document.
 *
 * The ID is now just the number key. The legacy scheme baked the uid into it
 * ('{uid}_{numberKey}') because a top-level collection had nowhere else to put
 * it, which in turn forced the rules to match on that prefix so a transaction
 * could probe a candidate that does not exist yet. The path carries the owner
 * now, so both went away.
 */
export const receiptNumberDocument = (uid: string, number: string) =>
  doc(db, 'users', uid, 'receiptNumbers', receiptNumberKey(number));

// ============================================================
// Per-user singletons
// ============================================================

// Deferred: these two are still top-level. Both are already keyed by uid, so
// neither has a tenant-isolation problem to solve, and both carry a Firestore
// document trigger (publicPageSync on companyDetails, subscription on
// userProfiles) that would have to be re-pointed in the same deploy. Moving
// them is its own change, not part of the data cutover.
export const companyDetailsDocument = (uid: string) =>
  doc(db, 'companyDetails', uid);

export const userProfileDocument = (uid: string) =>
  doc(db, 'userProfiles', uid);

export const publicPageOwnerDocument = (uid: string) =>
  doc(db, 'publicPageOwners', uid);

// ============================================================
// Not tenant-scoped — these stay top-level after the migration
// ============================================================

export const publicPageDocument = (slug: string) =>
  doc(db, 'publicPages', slug);

export const slugDocument = (slug: string) => doc(db, 'slugs', slug);

export const slugsCollection = () => collection(db, 'slugs');
